import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, ButtonVariants, RowActionsMenu } from '@carboledger/ui';
import { MoreHorizontal } from '@carboledger/ui/icons';
import { CUSTOMER_TABS, CUSTOMER_TAB_PARAM, DEFAULT_CUSTOMER_TAB, ROUTES } from '@/app/routes';
import { DetailPageHeader } from '@/components/common';
import { EmptyState, LoadingOverlay } from '@/components/feedback';
import { useTranslations } from '@/hooks';
import { useGetCustomerQuery } from '../api/customersApi';
import { CustomerFormModal } from '../components/CustomerFormModal';
import { CustomerSitesTabView } from '../components/CustomerSitesTabView';
import { CustomerTransportationTabView } from '../components/CustomerTransportationTabView';
import { TransportationFormModal } from '../components/TransportationFormModal';
import styles from './CustomerDetailsPage.module.scss';

const resolveTab = (value) =>
  Object.values(CUSTOMER_TABS).includes(value) ? value : DEFAULT_CUSTOMER_TAB;

export const CustomerDetailsPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { customerId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveTab(searchParams.get(CUSTOMER_TAB_PARAM));

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [transportTarget, setTransportTarget] = useState(null);

  const { data: customer, isLoading, isError } = useGetCustomerQuery(customerId);

  const goToCustomers = useCallback(() => navigate(ROUTES.customers), [navigate]);

  const handleTabChange = useCallback(
    (tab) => setSearchParams({ [CUSTOMER_TAB_PARAM]: tab }, { replace: true }),
    [setSearchParams],
  );

  const handleEdit = useCallback(() => setIsEditOpen(true), []);
  const handleCloseEdit = useCallback(() => setIsEditOpen(false), []);

  const handleAddSites = useCallback(
    () => navigate(ROUTES.customerSitesCreate(customerId)),
    [navigate, customerId],
  );

  const handleAddTransportation = useCallback(() => {
    handleTabChange(CUSTOMER_TABS.transportation);
    setTransportTarget({});
  }, [handleTabChange]);
  const handleEditTransportation = useCallback((route) => setTransportTarget({ route }), []);
  const handleCloseTransportation = useCallback(() => setTransportTarget(null), []);

  const tabs = useMemo(
    () => [
      { value: CUSTOMER_TABS.sites, label: t('CUSTOMER_DETAIL_TAB_SITES') },
      { value: CUSTOMER_TABS.transportation, label: t('CUSTOMER_DETAIL_TAB_TRANSPORTATION') },
    ],
    [t],
  );

  const breadcrumbItems = useMemo(
    () => [
      { label: t('HOME_MANAGE_CUSTOMERS'), onClick: goToCustomers },
      { label: customer?.customerName ?? '-' },
    ],
    [t, goToCustomers, customer?.customerName],
  );

  const menuItems = useMemo(
    () => [
      { id: 'edit', label: t('COMMON_EDIT'), onSelect: handleEdit },
      { id: 'add-sites', label: t('CUSTOMERS_ACTION_ADD_SITES'), onSelect: handleAddSites },
      {
        id: 'add-transportation',
        label: t('CUSTOMERS_ACTION_ADD_TRANSPORTATION'),
        onSelect: handleAddTransportation,
      },
    ],
    [t, handleEdit, handleAddSites, handleAddTransportation],
  );

  const headerActions = useMemo(
    () => (
      <RowActionsMenu
        items={menuItems}
        ariaLabel={t('COMMON_MORE_ACTIONS')}
        icon={MoreHorizontal}
      />
    ),
    [t, menuItems],
  );

  const metaFields = useMemo(() => {
    if (!customer) return [];
    return [
      { id: 'customerId', label: t('FIELD_CUSTOMER_ID'), value: customer.customerId },
      { id: 'contractNumber', label: t('FIELD_CONTRACT_NUMBER'), value: customer.contractNumber },
      {
        id: 'primaryContactName',
        label: t('FIELD_PRIMARY_CONTACT_NAME'),
        value: customer.primaryContactName,
      },
      { id: 'primaryEmail', label: t('FIELD_PRIMARY_CONTACT_EMAIL'), value: customer.primaryEmail },
      { id: 'addressLine1', label: t('FIELD_ADDRESS_LINE_1'), value: customer.addressLine1 },
      { id: 'addressLine2', label: t('FIELD_ADDRESS_LINE_2'), value: customer.addressLine2 },
      { id: 'city', label: t('FIELD_CITY_NAME'), value: customer.city },
      { id: 'country', label: t('FIELD_COUNTRY_NAME'), value: customer.country },
      { id: 'zipCode', label: t('FIELD_ZIP_CODE'), value: customer.zipCode },
      { id: 'lastUpdated', label: t('FIELD_LAST_UPDATED'), value: customer.lastUpdated },
    ];
  }, [t, customer]);

  if (isLoading) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  if (isError || !customer) {
    return (
      <EmptyState
        title={t('CUSTOMERS_LOAD_ERROR')}
        action={
          <Button variant={ButtonVariants.secondary} onClick={goToCustomers}>
            {t('COMMON_BACK')}
          </Button>
        }
      />
    );
  }

  return (
    <div className={styles.page}>
      <DetailPageHeader
        breadcrumbItems={breadcrumbItems}
        title={customer.customerName}
        actions={headerActions}
        metaFields={metaFields}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        tabsAriaLabel={t('HOME_MANAGE_CUSTOMERS')}
      />

      <div className={styles.content}>
        {activeTab === CUSTOMER_TABS.sites && (
          <CustomerSitesTabView customer={customer} onAddSites={handleAddSites} />
        )}

        {activeTab === CUSTOMER_TABS.transportation && (
          <CustomerTransportationTabView
            customer={customer}
            onAddTransportation={handleAddTransportation}
            onEditTransportation={handleEditTransportation}
          />
        )}
      </div>

      <CustomerFormModal open={isEditOpen} customerId={customer.id} onClose={handleCloseEdit} />

      <TransportationFormModal
        open={transportTarget !== null}
        customerId={customer.id}
        route={transportTarget?.route}
        onClose={handleCloseTransportation}
      />
    </div>
  );
};
